import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { UserService } from 'src/app/Account/user.service';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';
import { InstanceRights, RestrictService } from 'src/app/Common/Permissions/restrict.service';
import { InstanceRepository } from 'src/app/Entity/Instance/instance.repository';
import {
    MessageBoxButtons,
    MessageBoxResult,
    MessageBoxService,
} from 'src/app/UI-Lib/Message-Box/messagebox.service.upgrade';
import { CommentsModalService } from '../../Comments/commentsModal.service';
import { ContactsUpgradeService } from '../../Contact/contacts.service.upgrade';
import { InstancePermissionService } from '../../Permission/Instance/Instance.Permission.Service';
import { AccountService } from '../account.service';
import { UserInstanceService } from '../userInstance.service';
import { UserSetup, UserSetupService } from '../userSetup.service';

import * as _ from 'lodash';

@Component({
    selector: 'user-setup',
    templateUrl: './userSetup.component.html',
    standalone: false,
})
export class UserSetupComponent implements OnInit {
    constructor(
        private readonly _featureFlagsService: FeatureFlagsService,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _restrictService: RestrictService,
        private readonly _instancePermissionService: InstancePermissionService,
        private readonly _userSetupService: UserSetupService,
        private readonly _instanceRepository: InstanceRepository,
        private readonly _accountService: AccountService,
        private readonly _userService: UserService,
        private readonly _contactsService: ContactsUpgradeService,
        private readonly _commentsModalService: CommentsModalService,
        private readonly _messageBoxService: MessageBoxService,
        private readonly _toastr: ToastrService,
    ) {}

    @Input() contact: Weissman.Model.Contacts.Contact;
    @Input() tabSelected;
    @Input() modalRef: BsModalRef;

    @Output() isEditing = new EventEmitter<boolean>();

    userToRevertTo: UserSetup;
    featureFlags = this._featureFlagsService.featureFlags;

    instanceId = null;
    editMode: boolean;
    configuringSSO: boolean;
    newSSODomain = null;
    userId = null;
    userToEdit: UserSetup;
    canEdit: boolean;
    canEditUserInfo: boolean;
    canResetPassword: boolean;
    availableInstanceRights = [];
    permissionsLookup = [];
    contactStatuses = [];
    ssoDomains: string[] = [];
    newSSODomains: string[] = [];
    inSupportInstance: boolean;
    // Only show the Create Legacy User button if this is dev or staging/uat (staging and UAT both use 'S' as the environment character)
    showCreateLegacyUser: boolean;
    showLoginManager: boolean;
    isLegacyUser: boolean;
    isLicensedUser: boolean;
    showActiveCheckbox: boolean;
    iamMigrationStatus = 'Not Migrated';
    isLoading: boolean = true;
    invalid: boolean;
    needTaxDotComPermission: boolean;

    options = [];

    private readonly OPT_HIDE = 'Hide';
    private readonly OPT_VIEW_ALL = 'View All Sites';
    private readonly OPT_VIEW_EDIT_SOME = 'View/Edit Some Sites';
    private readonly OPT_EDIT_ALL = 'Edit All Sites';

    async ngOnInit(): Promise<void> {
        this.options = [this.OPT_HIDE, this.OPT_VIEW_ALL, this.OPT_VIEW_EDIT_SOME, this.OPT_EDIT_ALL];
        this.showCreateLegacyUser =
            this.featureFlags.enableIAM &&
            (this.featureFlags.environment === 'D' || this.featureFlags.environment === 'S');
        this.showLoginManager = true;
        this.inSupportInstance = this._userInstanceService.isCurrentInstanceSystem();

        this.canEdit = true; //accountUserService.isInRole(accountUserService.Roles.USERSEDIT);
        this.canEditUserInfo =
            this._restrictService.hasInstanceRight(
                InstanceRights.MANAGEUSERSETUP,
                this._userInstanceService.SystemInstanceId,
            ) && this.inSupportInstance;
        this.userId = this.contact.userID;
        this.instanceId = this.contact.company.instanceID;
        this.canResetPassword =
            this._restrictService.hasInstanceRight(InstanceRights.MANAGEUSERSETUP, this.instanceId) &&
            (!this._featureFlagsService.featureFlags.enableIAM || !this.contact.isMigrated);
        this.showActiveCheckbox = !this._featureFlagsService.featureFlags.enableIAM;

        if (this.contact.iamMigrationStatus === 1 /* Core.ContactIamStatusEnum.Onboarding */) {
            this.iamMigrationStatus = 'Onboarding';
        } else if (this.contact.iamMigrationStatus === 2 /* Core.ContactIamStatusEnum.Migrated */) {
            this.iamMigrationStatus = 'Migrated';
        }

        //get available instance rights
        const r = await this._instancePermissionService.getAllByContactId(this.contact.contactID);
        this.availableInstanceRights = r;
        this.permissionsLookup = structuredClone(r);

        if (this.userId) {
            //get user
            const user = await this._userSetupService.getUserData(this.userId, this.contact.contactID);
            this._setUser(user);
        } else {
            this.userToEdit = {
                id: null,
                userName: null,
                description: null,
                instanceId: null,
                ssoDomain: null,
                lockoutEnabled: false,
                active: true,
                userToGroup: [],
                roles: [],
                permissions: [],
                instancePermissions: [],
            };
        }

        if (this.canEditUserInfo) {
            if (this.userId) {
                this._instanceRepository.getUserContactInviteStatuses(this.userId).then((cs) => {
                    this.contactStatuses = [];
                    if (cs) {
                        this.contactStatuses = cs.filter((c) => c.active);
                    }
                });
            } else {
                this.contactStatuses = [
                    {
                        instanceId:
                            this.contact &&
                            this.contact.company &&
                            this.contact.company.instance &&
                            this.contact.company.instance.instanceID,
                        instanceName:
                            this.contact &&
                            this.contact.company &&
                            this.contact.company.instance &&
                            this.contact.company.instance.name,
                    },
                ];
            }
        }

        this.isLicensedUser = this._accountService.userData.isLicensedUser;
        this.isLoading = false;
    }

    //BFM WK-2639
    async emailPasswordReset(userName: string): Promise<void> {
        const result = await this._messageBoxService.open({
            message: 'This will generate a new password and email it to this user. Are you sure?',
            buttons: MessageBoxButtons.OKCancel,
        });

        if (result === MessageBoxResult.OK) {
            try {
                await this._userService.emailPasswordReset(userName);
                this._toastr.success('Password generated and emailed to user');
            } catch (err) {
                this._toastr.error('There was an error generating the password or emailing it to the user');
                this.isLoading = false;
            }
        }

        this.isLoading = false;
        this.editMode = false;
        this.isEditing.emit(false);
    }

    editUser(): void {
        this.configuringSSO = false;
        this.newSSODomain = null;
        this.editMode = true;
        this.isEditing.emit(true);
    }

    cancelEdit(): void {
        this.editMode = false;
        this.isEditing.emit(false);
        this.userToEdit = structuredClone(this.userToRevertTo);
    }

    editingUser(): boolean {
        return this.userId || this.editMode;
    }

    async createUser(isLegacyUser: boolean): Promise<void> {
        this.isLegacyUser = isLegacyUser;

        if (!this.contact || !this.contact.address || !this.contact.address.email) {
            this._toastr.error(
                'Email is required; please edit contact info, enter valid email address and try again',
                '',
            );
            return;
        }

        this.isLoading = true;

        try {
            let isAdmin: boolean;
            if (isLegacyUser) {
                // Essentially a no-op; you don't have to be an IAM admin to create a legacy user
                isAdmin = true;
            } else {
                isAdmin = await this._userSetupService.isIAMAdmin();
            }

            if (!isAdmin) {
                this.needTaxDotComPermission = true;
            } else {
                await this._setupSSODropdown();
                this.ssoDomains[0] = '(no SSO)';
                this.userToEdit.ssoDomain = this.ssoDomains[0];
                this.editMode = true;
                this.isEditing.emit(true);
            }
        } finally {
            this.isLoading = false;
        }

        this.userToEdit.instanceId = this.instanceId;
        this.userToEdit.userName = this.contact.address.email;
    }

    async saveUser() {
        this.isLoading = true;

        //editing existing user
        if (this.userId) {
            this.configuringSSO = false;
            if (this.userToEdit.ssoDomain === '(disable)') {
                this.userToEdit.ssoDomain = null;
            }
            if (this.newSSODomain) {
                this.userToEdit.ssoDomain = this.newSSODomain;
                this.newSSODomain = null;
            }
            const userToSave = this._prepareUser();

            try {
                await this._userService.updateUser(userToSave);
                //console.log('user info saved; result: ', result);

                this.userToEdit.userToGroup = this._cleanUpEFAction(this.userToEdit.userToGroup);
                this.userToEdit.permissions = this._cleanUpEFAction(this.userToEdit.permissions);

                this._mapPermissions(this.userToEdit.permissions);

                // We used to do this in the old user service; it's been moved. Also, it never took an argument,
                // so I think this probably should somehow detect when the user changed their own roles. Also,
                // does this need to be done at all? It seems like nonsense to me; users can't add new roles
                // to themselves, and would rarely remove them from themselves.
                //accountUserService.resetRoles(userToSave);
                this._accountService.resetRoles();

                this.userToRevertTo = structuredClone(this.userToEdit);
                this.editMode = false;
                this.isEditing.emit(false);
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                error.message = 'Sorry, there was a problem updating this user.';
                this._toastr.error(error.error.message);
            }
        } else {
            //creating new user
            this.userToEdit.lockoutEnabled = true;
            if (this.userToEdit.ssoDomain === this.ssoDomains[0]) {
                this.userToEdit.ssoDomain = null;
            }

            this.userToEdit.isLegacyUser = this.isLegacyUser;

            const mbMessage = 'You are about to create a new user from this contact Are you sure?';
            const mbResult = await this._messageBoxService.open({
                title: 'Confirm Create User',
                message: mbMessage,
                buttons: MessageBoxButtons.YesNo,
            });

            if (mbResult !== MessageBoxResult.Yes) {
                this.isLoading = false;
                this.editMode = true;
                this.isEditing.emit(true);
                return;
            }

            try {
                const userToSave = structuredClone(this.userToEdit);
                if (!userToSave.id) {
                    delete userToSave.id;
                }

                const user = await this._contactsService.createUser(this.contact.contactID, userToSave);
                this.contact.userID = user.id;
                const result = await this._userSetupService.getUserData(user.id, this.contact.contactID);
                this._setUser(result);

                //send email only for non-sso users being created (user's home instance is the current instance - brand new user)
                if (
                    !this.userToEdit.ssoDomain &&
                    (!this._featureFlagsService.featureFlags.enableIAM || this.isLegacyUser) &&
                    this.userToEdit.instanceId === this._userInstanceService.getSelectedInstance().instanceId
                ) {
                    await this.emailPasswordReset(result.userName);
                } else {
                    this.isLoading = false;
                    this._toastr.success('User saved');
                }
            } catch (error) {
                this.isLoading = false;
                console.log(error);
                let message = 'Sorry, there was a problem updating this user.';

                if (error.status === 409) {
                    message = 'User Name is already in use with another Contact.';
                } else if (error.error) {
                    message = error.error.message;
                }

                this._toastr.error(message);
                this.editMode = true;
                this.isEditing.emit(true);
            }
        }
    }

    checkForSpaces(userName: string): void {
        this.invalid = userName.indexOf(' ') !== -1;
    }

    async showDescriptionDialog(): Promise<void> {
        const commentBody = await this._commentsModalService.openAddCommentModal({
            title: `User: ${this.userToEdit.userName}`,
            commentBody: this.userToEdit.description,
            readOnly: !this.canEdit,
            allowBlank: true,
        });
        if (commentBody) {
            await this._userService.setUserDescription(this.contact.userID as number, commentBody);
            this.userToEdit.description = commentBody;
            this._toastr.success('User comments saved');
        }
    }

    cancelConfigureSSO(): void {
        this.configuringSSO = false;
        this.newSSODomain = null;
    }

    getHomeInstanceName(): string {
        if (this.userToEdit && this.userToEdit.instanceId && this.contactStatuses) {
            const currentHomeInstance = this.contactStatuses.find((c) => c.instanceId == this.userToEdit.instanceId);
            return currentHomeInstance && currentHomeInstance.instanceName;
        }
    }

    goToLoginManager(): void {
        this.modalRef.hide();
    }

    // this takes the permission attributes and puts them into a 'permission' attribute
    // which can be mapped to a dropdown list value
    /*
    const OPT_HIDE = 'Hide';
    const OPT_VIEW_ALL = 'View All Sites';
    const OPT_VIEW_EDIT_SOME = 'View/Edit Some Sites';
    const OPT_EDIT_ALL = 'Edit All Sites';
    */
    private _mapPermissions(permissions): void {
        if (permissions === undefined) return;

        permissions.forEach((p) => {
            if (!p.writeAllowed && !p.readAllowed) {
                p.permission = this.OPT_HIDE;
            }

            if (p.writeAllowed) {
                if (p.includeAllChildRecords) {
                    p.permission = this.OPT_EDIT_ALL;
                } else {
                    p.permission = this.OPT_VIEW_EDIT_SOME;
                }
            } else if (p.readAllowed) {
                if (p.includeAllChildRecords) {
                    p.permission = this.OPT_VIEW_ALL;
                } else {
                    p.permission = this.OPT_VIEW_EDIT_SOME;
                }
            }
        });
    }

    private _cleanUpEFAction(entities) {
        return entities
            .filter((x) => x.efAction !== 'delete')
            .map((x) => {
                x.efAction = null;
                return x;
            });
    }

    private _setUser(result: UserSetup): void {
        this.userToEdit = result;
        this.userId = result.id;

        this.userToEdit.userToGroup = this._cleanUpEFAction(this.userToEdit.userToGroup);
        this.userToEdit.permissions = this._cleanUpEFAction(this.userToEdit.permissions);
        this.userToEdit.instancePermissions = this._cleanUpEFAction(this.userToEdit.instancePermissions);

        this._mapPermissions(this.userToEdit.permissions);
        this._mapPermissions(this.userToEdit.instancePermissions);

        this.userToRevertTo = structuredClone(this.userToEdit);

        this.editMode = false;
        this.isEditing.emit(false);
        this._setupSSODropdown();
    }

    private async _setupSSODropdown(): Promise<void> {
        const ssoDomains = await this._userSetupService.getSSODomains(this.contact.contactID);
        let domains = [];
        if (this.userToEdit.ssoDomain && ssoDomains.indexOf(this.userToEdit.ssoDomain) < 0) {
            domains.push(this.userToEdit.ssoDomain);
        }
        domains = domains.concat(ssoDomains);
        this.newSSODomains = domains;
        this.ssoDomains = ['(disable)'].concat(domains);
    }

    private _prepareUser(): Core.UserSetupUpdateDTO {
        const editedUser = structuredClone(this.userToEdit);
        if (!editedUser.id) {
            delete editedUser.id;
        }

        //roles
        const roles = editedUser.roles.map((role) => {
            return {
                roleID: role.roleID,
                userID: editedUser.id,
            };
        });

        //user to group
        const userToGroup = editedUser.userToGroup.map((userGroup) => {
            return {
                groupID: userGroup.groupID,
                efAction: userGroup['efAction'],
                userID: editedUser.id,
            } as Weissman.Model.UserSetupUpdateGroupDTO;
        });

        //global roles (app permisisons)
        let permissions = editedUser.permissions
            .filter((p) => p.efAction !== null)
            .map((permission) => {
                permission.userId = editedUser.id;
                return permission;
            });
        permissions = _.uniqWith(permissions, _.isEqual);
        //instance rights - leave them alone

        return {
            ...editedUser,
            contactID: this.contact.contactID,
            roles,
            userToGroup,
            permissions,
            demoUser: false,
        } as any as Core.UserSetupUpdateDTO;
    }
}
